import * as THREE from 'three';
import { TimelineLite, Power2 } from 'gsap';
import Sequence from '@/models/Sequence';
import grayscaleFragment from '../scene/Shader/grayscale.frag';
import baseVertex from '../scene/Shader/base.vert';

export default class Detail3D extends THREE.Object3D {
  constructor() {
    super();

    this.animation = {
      transitionX: 0,
      transitionY1: 1,
      transitionY2: 1,
    };
    this.canChange = new Promise(resolve => resolve());
    this.tween = new TimelineLite();
    this.image = undefined;
    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d');
    this.context.imageSmoothingEnabled = true;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.minFilter = THREE.LinearFilter;
    this.sequence = undefined;
    this.details = [
      new THREE.Mesh(
        new THREE.PlaneBufferGeometry(0.00001, 0.00001),
        new THREE.RawShaderMaterial({
          fragmentShader: grayscaleFragment,
          vertexShader: baseVertex,
          uniforms: {
            texture: {
              value: this.texture,
            },
            opacity: {
              value: 1,
            },
            frequency: {
              value: 2.2,
            },
            gradientstart: {
              value: new THREE.Vector3(0, 0, 0),
            },
            gradientend: {
              value: new THREE.Vector3(0, 0, 0),
            },
            resolution: {
              value: new THREE.Vector2(0, 0),
            },
          },
        }),
      ),
      new THREE.Mesh(
        new THREE.PlaneBufferGeometry(0.00001, 0.00001),
        new THREE.RawShaderMaterial({
          fragmentShader: grayscaleFragment,
          vertexShader: baseVertex,
          uniforms: {
            texture: {
              value: this.texture,
            },
            opacity: {
              value: 1,
            },
            frequency: {
              value: 3,
            },
            gradientstart: {
              value: new THREE.Vector3(0, 0, 0),
            },
            gradientend: {
              value: new THREE.Vector3(0, 0, 0),
            },
            resolution: {
              value: new THREE.Vector2(0, 0),
            },
          },
        }),
      ),
    ];

    this.details.forEach(d => this.add(d));

    this.image = {
      width: window.innerWidth / window.innerHeight,
      height: 1,
    };
    this.details[0].position.x = 100;
    this.details[1].position.x = 100;
  }
  show() {
    this.canChange = new Promise((resolve) => {
      this.tween.pause();
      this.tween.clear();
      this.tween.play();
      this.tween
        .to(this.animation, 1.1, {
          transitionY1: 0,
          ease: Power2.easeOut,
        }, 0.3)
        .to(this.animation, 1.1, {
          transitionY2: 0,
          ease: Power2.easeOut,
          onComplete: () => resolve(),
        }, '-=0.7');
    });
  }
  hide() {
    this.canChange = new Promise((resolve) => {
      this.tween.pause();
      this.tween.clear();
      this.tween.play();
      this.tween
        .to(this.animation, 0.6, {
          transitionY1: 1,
          ease: Power2.easeIn,
        })
        .to(this.animation, 0.6, {
          transitionY2: 1,
          ease: Power2.easeIn,
          onComplete: () => resolve(),
        }, '-=0.7');
    });
  }
  setSequence(sequence) {
    return new Promise((resolve, reject) => {
      if (sequence instanceof Sequence) {
        this.sequence = sequence;
        this.canChange
          .then(() => this.sequence.getTexture())
          .then(image => this.setImage(image))
          .then(() => this.update())
          .then(() => resolve())
          .catch(e => reject(e));
      } else reject();
    });
  }
  setImage(image) {
    this.canvas.width = image.width;
    this.canvas.height = image.height;
    this.canvas.style.width = `${image.width}px`;
    this.canvas.style.height = `${image.height}px`;
    this.context.drawImage(image, 0, 0, image.width, image.height);
    this.texture.needsUpdate = true;
  }
  resize() {
    this.image = {
      width: window.innerWidth / window.innerHeight,
      height: 2,
    };

    this.details[0].geometry.attributes.position.set([
      -0.5 * this.image.width,
      0.2,
      0,
      0,
      0.2,
      0,
      -0.5 * this.image.width,
      -0.5,
      0,
      0,
      -0.5,
      0,
    ]);
    this.details[0].geometry.attributes.uv.set([
      1 - (0.5 * this.image.width), 1,
      1, 1,
      1 - (0.5 * this.image.width), 0,
      1, 0,
    ]);
    this.details[0].geometry.attributes.position.needsUpdate = true;
    this.details[0].geometry.attributes.uv.needsUpdate = true;

    this.details[1].geometry.attributes.position.set([
      0.25 * this.image.width,
      0.5,
      0,
      0.5 * this.image.width,
      0.5,
      0,
      0.25 * this.image.width,
      -0.1,
      0,
      0.5 * this.image.width,
      -0.1,
      0,
    ]);
    this.details[1].geometry.attributes.uv.set([
      0, 1,
      0.25 * this.image.width, 1,
      0, 0,
      0.25 * this.image.width, 0,
    ]);
    this.details[1].geometry.attributes.position.needsUpdate = true;
    this.details[1].geometry.attributes.uv.needsUpdate = true;
  }
  update() {
    this.resize();
    this.details.forEach((d) => {
      const detail = d;

      detail.material.uniforms.resolution.value.set(this.image.width, this.image.height);
      detail.material.uniforms.resolution.needsUpdate = true;

      detail.material.uniforms.gradientstart.value.set(
        this.sequence.colors.nStart[0],
        this.sequence.colors.nStart[1],
        this.sequence.colors.nStart[2],
      );
      detail.material.uniforms.gradientend.value.set(
        this.sequence.colors.nEnd[0],
        this.sequence.colors.nEnd[1],
        this.sequence.colors.nEnd[2],
      );
      detail.material.uniforms.gradientstart.needsUpdate = true;
      detail.material.uniforms.gradientend.needsUpdate = true;
    });
  }
  animate() {
    this.details[0].position.x = this.animation.transitionX * -0.5 * this.image.width;
    this.details[0].position.y = this.animation.transitionY1 * -0.7;

    this.details[1].position.x = this.animation.transitionX * 0.25 * this.image.width;
    this.details[1].position.y = this.animation.transitionY2 * 0.5;
  }
}



// WEBPACK FOOTER //
// ./src/scene/Detail3D.js